import {
	addressStateToString,
	CustomAddressInputField,
	DatePicker,
	DeletePopover,
	Divider,
	EMPTY_ADDRESS_STATE,
	Icon,
	IconButton,
	LabeledCheckbox,
	momentToIsoDateOnly,
	MuiAlert,
	Select,
	strIsNullOrEmpty,
	TextField,
	Typography
} from '@fox/atoms';
import { Flex, FlexRow } from '@fox/layout';
import { LinearProgress } from '@mui/material';
import MUITooltip from '@mui/material/Tooltip';
import { Field, FormikErrors, FormikTouched } from 'formik';
import { E164Number } from 'libphonenumber-js';
import * as moment from 'moment';
import { Moment } from 'moment';
import React, { forwardRef, useCallback, useState } from 'react';
import PhoneInput from 'react-phone-number-input/input';
import styled, { useTheme } from 'styled-components';
import { tryParseJSONObject } from '../../../common/helper/helper';
import { ContactTypeRule } from '../profiles/shared/contracts';
import { ResponsiveGrid } from '../profiles/shared/profile-style-components';
import { Address, ContactDetail, CustomFieldValue, MembershipFormField } from './contracts';
import { CheckboxCustomField } from './edit-custom-fields/check-box';
import { DateCustomField } from './edit-custom-fields/datetime';
import { FileUploadCustomField } from './edit-custom-fields/fileupload';
import { LinkCustomField } from './edit-custom-fields/link';
import { NumberCustomField } from './edit-custom-fields/number';
import { SelectCustomField } from './edit-custom-fields/select';
import { StringCustomField } from './edit-custom-fields/string';

export const FieldFlexWrapper = styled.div`
	display: flex;
	flex-direction: column;
	padding: 1rem 0rem;
`;

const FieldWithOptionsWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 1rem;
	.fieldDivider {
		display: none;
	}
	@media (max-width: 400px) {
		flex-direction: column;
		.appearOnHover {
			opacity: 1;
		}
		.fieldDivider {
			display: block;
		}
	}
`;

type MembershipFormFieldProps<T, Y = void> = {
	field: MembershipFormField;
	setFieldValue?: (field: string, value: any) => void;
	helperText?: string;
	error?: boolean;
	value?: T;
	isEditing?: boolean;
	addressTypes?: string[];
	editableAddressTypes?: string[];
	contactTypeRules?: ContactTypeRule[];
	errors?: FormikErrors<Y>;
	touched?: FormikTouched<Y>;
};

export function MembershipFormCheckboxField({ field, error, helperText, setFieldValue, value }: MembershipFormFieldProps<boolean>) {
	const theme = useTheme();
	return (
		<div>
			<Field
				style={{
					marginLeft: 0,
					marginRight: 0,
					marginBottom: 0,
					cursor: 'default',
					color: error ? theme.palette.error.main : theme.colors.font
				}}
				checked={value}
				key={field.name}
				name={field.name}
				label={field.label}
				component={LabeledCheckbox}
				checkboxProps={{
					sx: {
						paddingLeft: '0px',
						'& .MuiSvgIcon-root': { fontSize: 24, cursor: 'pointer' }
					}
				}}
				error={error}
				onClick={() => {
					setFieldValue(field.name, !value);
				}}
			/>
			<span
				style={{
					color: error ? theme.palette.error.main : theme.colors.font,
					display: 'block'
				}}
			>
				{helperText}
			</span>
		</div>
	);
}

export function MembershipFormTextField({ field, helperText, error, setFieldValue, value, isEditing }: MembershipFormFieldProps<string>) {
	if (isEditing == false) {
		return (
			<div>
				<FieldFlexWrapper>
					<Typography color="text.disabled">{field.label}</Typography>
					<Typography>{value || '-'}</Typography>
				</FieldFlexWrapper>
				<Divider />
			</div>
		);
	}
	return (
		<Field
			key={field.name}
			name={field.name}
			label={field.label}
			component={TextField}
			error={error}
			value={value}
			helperText={helperText}
			onChange={(event: React.FormEvent<HTMLInputElement>) => {
				setFieldValue(field.name, event.currentTarget.value);
			}}
		/>
	);
}

export function MembershipFormPasswordField({ field, helperText, error, setFieldValue }: MembershipFormFieldProps<string>) {
	const [showText, setShowText] = useState<boolean>(false);
	return (
		<Field
			key={field.name}
			name={field.name}
			label={field.label}
			component={TextField}
			error={error}
			helperText={helperText}
			onChange={(event: React.FormEvent<HTMLInputElement>) => {
				setFieldValue(field.name, event.currentTarget.value);
			}}
			type={showText ? 'text' : 'password'}
			endAdornment={
				showText ? (
					<IconButton iconName="VisibilityOff" onClick={() => setShowText(false)} />
				) : (
					<IconButton iconName="Visibility" onClick={() => setShowText(true)} />
				)
			}
		/>
	);
}

export function MembershipFormDateField({ field, helperText, error, setFieldValue, value, isEditing }: MembershipFormFieldProps<Date>) {
	const valueDate = value ? moment(value) : null;
	if (isEditing == false) {
		return (
			<div>
				<FieldFlexWrapper>
					<Typography color="text.disabled">{field.label}</Typography>
					<Typography>{valueDate ? moment(valueDate).format('DD MMMM YYYY') : ''}</Typography>
				</FieldFlexWrapper>
				<Divider />
			</div>
		);
	}

	return (
		<Field
			key={field.name}
			name={field.name}
			label={field.label}
			component={DatePicker}
			includeTime={false}
			error={error}
			value={valueDate}
			helperText={helperText}
			onChange={(value: Moment) => {
				setFieldValue(field.name, momentToIsoDateOnly(value));
			}}
		/>
	);
}

export function MembershipFormPasswordWithStrengthIndicatorField({
	field,
	helperText,
	error,
	setFieldValue
}: MembershipFormFieldProps<string>) {
	const [showText, setShowText] = useState<boolean>(false);
	const [progress, setProgress] = useState<{
		'8 characters': boolean;
		'1 uppercase': boolean;
		'1 special character': boolean;
		'1 number': boolean;
		'1 lowercase': boolean;
	}>({
		'8 characters': false,
		'1 uppercase': false,
		'1 special character': false,
		'1 number': false,
		'1 lowercase': false
	});

	const checkPasswordStrength = (password: string) => {
		setProgress(prev => {
			prev['8 characters'] = password.length > 7 ? true : false;
			prev['1 uppercase'] = /[A-Z]/.test(password) ? true : false;
			prev['1 lowercase'] = /[a-z]/.test(password) ? true : false;
			prev['1 special character'] = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(password) ? true : false;
			prev['1 number'] = /[0-9]/.test(password) ? true : false;
			return prev;
		});
	};

	const checkProgress = useCallback(() => {
		return progress['8 characters'] ? Object.values(progress).filter(key => key === true).length * 20 : 0;
	}, [progress]);

	return (
		<>
			<Field
				key={field.name}
				name={field.name}
				label={field.label}
				component={TextField}
				error={error}
				helperText={helperText}
				onChange={(event: React.FormEvent<HTMLInputElement>) => {
					setFieldValue(field.name, event.currentTarget.value);
					checkPasswordStrength(event.currentTarget.value);
				}}
				type={showText ? 'text' : 'password'}
				endAdornment={
					showText ? (
						<IconButton iconName="VisibilityOff" onClick={() => setShowText(false)} />
					) : (
						<IconButton iconName="Visibility" onClick={() => setShowText(true)} />
					)
				}
			/>
			<LinearProgress
				variant="determinate"
				value={checkProgress()}
				color={
					progress['8 characters'] ? (checkProgress() < 60 ? 'error' : checkProgress() < 100 ? 'warning' : 'success') : 'error'
				}
			/>
			<Typography>{`Password strength: ${checkProgress() < 60 ? 'Bad' : checkProgress() < 100 ? 'Medium' : 'Good'}`}</Typography>
			{Object.entries(progress).map(([key, value], index) => {
				return (
					<FlexRow key={index}>
						<Icon iconName="Done" color="success" sx={{ visibility: value ? 'visible' : 'hidden' }} />
						<Typography>{`${key} ${index === 0 ? '(required)' : ''}`}</Typography>
					</FlexRow>
				);
			})}
		</>
	);
}

export function MembershipAddressesField<Y>({
	field,
	setFieldValue,
	value,
	isEditing,
	addressTypes,
	errors,
	touched,
	editableAddressTypes
}: MembershipFormFieldProps<Address[], Y>) {
	const addressesTouched = touched?.[field.name];
	const addresses = value?.filter(v => !!v.trackingId) ?? [];

	if (addresses.length === 0) {
		return <MuiAlert severity="info">No addresses to display</MuiAlert>;
	}

	return (
		<ResponsiveGrid>
			{isEditing == false
				? value
						?.filter(v => !!v.trackingId)
						?.map(address => {
							return (
								<div key={`nonEdit${address.trackingId}`}>
									<FieldFlexWrapper>
										<Typography color="text.disabled">{address.addressTypeName}</Typography>
										<Typography>
											{address.address1
												? `${address.address1}, ${address.locality}, ${address.postcode}, ${address.state}`
												: '-'}
										</Typography>
									</FieldFlexWrapper>
									<Divider />
								</div>
							);
						})
				: addresses?.map((address, i) => {
						const error = errors?.[field.name]?.[i]; //dont like this but what else can we do here? (without some seriously hacky stuff)
						const deleteAddress = () => {
							const newValue = [...value.filter(v => v.trackingId !== address.trackingId)];
							setFieldValue(field.name, newValue);
						};
						if (!editableAddressTypes.includes(address.addressTypeName)) {
							return (
								<div key={`edit${address.trackingId}`}>
									<FieldFlexWrapper>
										<Typography color="text.disabled">{address.addressTypeName}</Typography>
										<Typography>
											{address.address1
												? `${address.address1}, ${address.locality}, ${address.postcode}, ${address.state}`
												: '-'}
										</Typography>
									</FieldFlexWrapper>
									<Divider />
								</div>
							);
						}
						return (
							<FieldWithOptionsWrapper key={`edit${address.trackingId}`}>
								<Select
									sx={{ minWidth: '150px' }}
									label="Address type"
									disableClearable={true}
									autoComplete={false}
									options={editableAddressTypes}
									value={address?.addressTypeName ?? editableAddressTypes[0]}
									onChange={(_, v) => {
										const newValue = [...value];
										const addressToEdit = newValue.find(v => v.trackingId === address.trackingId);
										const index = addressTypes.findIndex(a => a === v);
										addressToEdit.addressTypeId = index;
										addressToEdit.addressTypeName = v;
										setFieldValue(field.name, newValue);
									}}
								/>
								<Flex style={{ width: '100%', alignItems: 'flex-start' }}>
									<CustomAddressInputField
										size="medium"
										label={address.addressTypeName ?? 'Choose address type'}
										value={formatAddressValue(address)}
										fullSize={true}
										onChange={c => {
											const newValue = [...value];
											const addressToEdit = newValue.find(v => v.trackingId === address.trackingId);
											if (c) {
												addressToEdit.address1 = c.address1;
												addressToEdit.building = c.building;
												addressToEdit.country = c.country;
												addressToEdit.locality = c.locality;
												addressToEdit.postcode = c.postcode;
												addressToEdit.state = c.state;
											} else {
												addressToEdit.address1 = '';
												addressToEdit.building = '';
												addressToEdit.country = '';
												addressToEdit.locality = '';
												addressToEdit.postcode = '';
												addressToEdit.state = '';
												addressToEdit.formatted = '';
											}
											setFieldValue(field.name, newValue);
										}}
										error={addressesTouched && !!error}
										helperText={
											addressesTouched && !!error
												? `${error['address1'] ? error['address1'] : ''} ${
														error['addressType'] ? error['addressType'] : ''
												  }`
												: null
										}
										width="100%"
									/>
									{address.id !== null ? (
										<DeletePopover
											message={<div style={{ maxWidth: 300 }}>Are you sure you want to remove this address?</div>}
											confirm={deleteAddress}
											placement="bottom"
										>
											<MUITooltip title="Delete address">
												<IconButton iconName={'Delete'} style={{ marginTop: '10px' }} />
											</MUITooltip>
										</DeletePopover>
									) : (
										<MUITooltip title="Delete address">
											<IconButton onClick={deleteAddress} iconName={'Delete'} style={{ marginTop: '10px' }} />
										</MUITooltip>
									)}
								</Flex>
								<Divider className="fieldDivider" />
							</FieldWithOptionsWrapper>
						);
				  })}
		</ResponsiveGrid>
	);
}

const formatAddressValue = (address: Address) => {
	if (address.address1) {
		return {
			...address,
			formatted: addressStateToString(address)
		};
	} else {
		return { ...EMPTY_ADDRESS_STATE };
	}
};
const PhoneField = forwardRef<HTMLInputElement>((props, ref) => {
	return <TextField {...props} inputRef={ref} />;
});

export function MembershipContactsField<Y>({
	field,
	setFieldValue,
	value,
	isEditing,
	errors,
	touched,
	contactTypeRules
}: MembershipFormFieldProps<ContactDetail[], Y>) {
	var options = ['Phone', 'Mobile', 'Fax', 'Email', 'Web', 'LinkedIn', 'WeChat', 'Twitter', 'Facebook', 'Instagram'];
	const contactsTouched = touched?.[field.name];
	const contacts = value?.filter(v => !!v.trackingId);
	if (contacts.length === 0) {
		return <MuiAlert severity="info">No contact information to display</MuiAlert>;
	}

	const editableContactTypes = options.filter(o => {
		const typeRule = contactTypeRules.find(r => r.type === o);
		if (typeRule && typeRule.editable === false) {
			return false;
		}
		return true;
	});
	return (
		<ResponsiveGrid>
			{isEditing == false
				? value
						?.filter(v => !!v.trackingId)
						?.map(contactDetail => {
							return (
								<div key={contactDetail.trackingId}>
									<FieldFlexWrapper>
										<Typography color="text.disabled">{contactDetail.type}</Typography>
										<Typography>{contactDetail.value || '-'}</Typography>
									</FieldFlexWrapper>
									<Divider />
								</div>
							);
						})
				: contacts?.map((contactDetail, i) => {
						const error = errors?.[field.name]?.[i];
						const deleteContact = () => {
							const newValue = [...value.filter(v => v.trackingId !== contactDetail.trackingId)];
							setFieldValue(field.name, newValue);
						};
						if (!editableContactTypes.includes(contactDetail.type) && !strIsNullOrEmpty(contactDetail.type)) {
							return (
								<div key={contactDetail.trackingId}>
									<FieldFlexWrapper>
										<Typography color="text.disabled">{contactDetail.type}</Typography>
										<Typography>{contactDetail.value || '-'}</Typography>
									</FieldFlexWrapper>
									<Divider />
								</div>
							);
						}
						return (
							<FieldWithOptionsWrapper key={`edit${contactDetail.trackingId}`}>
								<Select
									sx={{ minWidth: '150px' }}
									label="Contact type"
									value={contactDetail.type}
									options={editableContactTypes}
									disableClearable={true}
									autoComplete={false}
									onChange={(_, v) => {
										const newValue = [...value];
										const contactToEdit = newValue.find(v => v.trackingId === contactDetail.trackingId);
										contactToEdit.type = v;
										setFieldValue(field.name, newValue);
									}}
								/>
								<Flex style={{ width: '100%', alignItems: 'flex-start' }}>
									{contactDetail.type === 'Mobile' || contactDetail.type === 'Phone' || contactDetail.type === 'Fax' ? (
										<PhoneInput
											onChange={c => {
												const newValue = [...value];
												const contactToEdit = newValue.find(v => v.trackingId === contactDetail.trackingId);

												contactToEdit.value = c;
												setFieldValue(field.name, newValue);
											}}
											value={contactDetail.value as E164Number}
											label={contactDetail.type}
											inputComponent={PhoneField}
											error={contactsTouched && !!error}
											helperText={
												contactsTouched && !!error
													? `${error['value'] ? error['value'] : ''} ${error['type'] ? error['type'] : ''}`
													: null
											}
											sx={{ width: '100%' }}
											defaultCountry="AU"
										/>
									) : (
										<TextField
											label={!strIsNullOrEmpty(contactDetail.type) ? contactDetail.type : 'Select contact type'}
											value={contactDetail.value}
											onChange={event => {
												const newValue = [...value];
												const contactToEdit = newValue.find(v => v.trackingId === contactDetail.trackingId);

												contactToEdit.value = event.target.value;
												setFieldValue(field.name, newValue);
											}}
											error={contactsTouched && !!error}
											helperText={
												contactsTouched && !!error
													? `${error['value'] ? error['value'] : ''} ${error['type'] ? error['type'] : ''}`
													: null
											}
											sx={{ width: '100%' }}
										/>
									)}
									{contactDetail.id !== null ? (
										<DeletePopover
											message={
												<div style={{ maxWidth: 300 }}>Are you sure you want to remove this contact detail?</div>
											}
											confirm={deleteContact}
											placement="bottom"
										>
											<MUITooltip title="Delete contact detail">
												<IconButton iconName={'Delete'} alignCenter={true} style={{ marginTop: '10px' }} />
											</MUITooltip>
										</DeletePopover>
									) : (
										<MUITooltip title="Delete contact detail">
											<IconButton
												onClick={deleteContact}
												iconName={'Delete'}
												alignCenter={true}
												style={{ marginTop: '10px' }}
											/>
										</MUITooltip>
									)}
								</Flex>
								<Divider className="fieldDivider" />
							</FieldWithOptionsWrapper>
						);
				  })}
		</ResponsiveGrid>
	);
}

export const FORM_FIELD_COMPONENT_MAP = {
	TextField: MembershipFormTextField,
	PasswordField: MembershipFormPasswordField,
	PasswordFieldWithStrength: MembershipFormPasswordWithStrengthIndicatorField,
	CheckboxField: MembershipFormCheckboxField,
	DateField: MembershipFormDateField,
	AddressesField: MembershipAddressesField,
	ContactsField: MembershipContactsField
};

export const CUSTOM_FIELD_COMPONENT_MAP = {
	string: StringCustomField,
	'check-box': CheckboxCustomField,
	number: NumberCustomField,
	select: SelectCustomField,
	datetime: DateCustomField,
	link: LinkCustomField,
	fileupload: FileUploadCustomField
};

function ReadOnlyCustomFieldValue(customField: CustomFieldValue): React.ReactNode {
	const value = CustomFieldValueConverter[customField.type]?.(customField.value, customField.meta);

	switch (customField.type) {
		case 'check-box':
			return customField.value === '1' ? <Icon iconName="Done" /> : <Icon iconName="Cancel" />;
		case 'fileupload':
			return <FileUploadCustomField customField={customField} readonly />;
		default:
			return <Typography>{value || '-'}</Typography>;
	}
}

export const CustomField: React.FC<{
	customField: CustomFieldValue;
	onChange?: (CustomFieldId: string, value: any) => void;
	isEditing?: boolean;
}> = ({ isEditing, onChange, customField }) => {
	if (isEditing === false || !Object.keys(CUSTOM_FIELD_COMPONENT_MAP).includes(customField.type) || !customField.editable) {
		return (
			<div>
				<FieldFlexWrapper>
					<Typography color="text.disabled">{customField.displayName}</Typography>
					{ReadOnlyCustomFieldValue(customField)}
				</FieldFlexWrapper>

				<Divider />
			</div>
		);
	} else {
		const Component = CUSTOM_FIELD_COMPONENT_MAP[customField.type];
		if (Component) {
			return <Component customField={customField} onChange={onChange} />;
		} else {
			return <Typography>No edit component for this field type!</Typography>;
		}
	}
};

export const CustomFieldValueConverter: Record<string, (value: unknown, meta?: string) => string> = {
	string: (value: string) => value,
	'auto-increment': (value: string) => value,
	'check-box': (value: string) => value,
	number: (value: string, meta: string) => {
		if (!value) return '';
		//at some stage needs to be better, but for now it'll do?
		const parsedMeta: { format: string } = tryParseJSONObject(meta);
		if (parsedMeta?.format) {
			const parsedFormat: { currency: string; style: string } = tryParseJSONObject(parsedMeta.format);
			return `${parsedFormat?.style === 'currency' ? '$' : ''}${value.toLocaleString()}`;
		} else {
			return value;
		}
	},
	fileupload: (value: { files: { fileName: string }[] }) => {
		try {
			return value?.files.map(a => a.fileName).join(', ');
		} catch (E) {
			return '';
		}
	},
	select: (value: { id: string; label: string }[]) => {
		if (!value) return '';
		return value?.map(v => v?.label)?.join(', ');
	},
	datetime: (value: string) => {
		if (!value) return '';
		const m = moment(value);
		return m.format('DD MMMM YYYY');
	},
	link: (value: { name: string }[]) => {
		if (!value) return '';
		return value.map(v => v?.name)?.join(', ');
	},
	smarturl: (value: { value: string }) => {
		if (strIsNullOrEmpty(value?.value)) {
			return '';
		}
		return value.value;
	}
};
